import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "lib/redux/store";

export interface UIState {
  openMobileResumeOverlay: boolean;
  userProfilePhoto: string | null;
  userId: number | null;
  oAuthOpenedFromButton:
    | "save"
    | "share"
    | "download"
    | "use Ai Suggestions"
    | "subscribe"
    | "resume-writing";
  subscriptionCreditCount: number;
  isPremiumUser: boolean;
  accessToken: string | null;
  isOutOfCreditsModalOpen: boolean;
  isAuthenticationOpen: boolean;
  isExplorePlansOverlayOpen: boolean;
  isResumeDownloading: boolean;
  dodoRedirectUrl: string;
  isScoringLoadingModalOpen: boolean;
}

export const initialUIState: UIState = {
  openMobileResumeOverlay: false,
  userProfilePhoto: null,
  userId: null,
  oAuthOpenedFromButton: "download",
  subscriptionCreditCount: 0,
  isPremiumUser: false,
  accessToken: null,
  isOutOfCreditsModalOpen: false,
  isAuthenticationOpen: false,
  isExplorePlansOverlayOpen: false,
  isResumeDownloading: false,
  dodoRedirectUrl: "",
  isScoringLoadingModalOpen: false,
};

export const uiSlice = createSlice({
  name: "ui",
  initialState: initialUIState,
  reducers: {
    setUI: (state, action: PayloadAction<UIState>) => {
      return action.payload;
    },
    setOpenMobileResumeOverlay: (state, action: PayloadAction<boolean>) => {
      state.openMobileResumeOverlay = action.payload;
    },
    setUserProfilePhoto: (state, action: PayloadAction<string | null>) => {
      state.userProfilePhoto = action.payload;
    },
    setUserId: (state, action: PayloadAction<number | null>) => {
      state.userId = action.payload;
    },
    setOAuthOpenedFromButton: (
      state,
      action: PayloadAction<
        | "save"
        | "share"
        | "download"
        | "use Ai Suggestions"
        | "subscribe"
        | "resume-writing"
      >
    ) => {
      state.oAuthOpenedFromButton = action.payload;
    },
    setSubscriptionCreditCount: (state, action: PayloadAction<number>) => {
      state.subscriptionCreditCount = action.payload;
    },
    setAccessToken: (state, action: PayloadAction<string | null>) => {
      state.accessToken = action.payload;
    },
    setIsPremiumUser: (state, action: PayloadAction<boolean>) => {
      state.isPremiumUser = action.payload;
    },
    setIsOutOfCreditsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isOutOfCreditsModalOpen = action.payload;
    },
    setIsAuthenticationOpen: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticationOpen = action.payload;
    },
    setIsExplorePlansOverlayOpen: (state, action: PayloadAction<boolean>) => {
      state.isExplorePlansOverlayOpen = action.payload;
    },
    setIsResumeDownloading: (state, action: PayloadAction<boolean>) => {
      state.isResumeDownloading = action.payload;
    },
    setDodoRedirectUrl: (state, action: PayloadAction<string>) => {
      state.dodoRedirectUrl = action.payload;
    },
    setIsScoringModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isScoringLoadingModalOpen = action.payload;
    },
  },
});

export const {
  setUI,
  setOpenMobileResumeOverlay,
  setUserProfilePhoto,
  setUserId,
  setOAuthOpenedFromButton,
  setSubscriptionCreditCount,
  setIsPremiumUser,
  setAccessToken,
  setIsOutOfCreditsModalOpen,
  setIsAuthenticationOpen,
  setIsExplorePlansOverlayOpen,
  setIsResumeDownloading,
  setDodoRedirectUrl,
  setIsScoringModalOpen,
} = uiSlice.actions;

// export uiSettings
export const selectUiSettings = (state: RootState) => state.ui;
export const selectOpenMobileResumeOverlay = (state: RootState) =>
  state.ui.openMobileResumeOverlay;
export const selectUserProfilePhoto = (state: RootState) =>
  state.ui.userProfilePhoto;
export const selectUserId = (state: RootState) => state.ui.userId;
export const selectOAuthOpenedFromButton = (state: RootState) =>
  state.ui.oAuthOpenedFromButton;
export const selectSubscriptionCreditCount = (state: RootState) =>
  state.ui.subscriptionCreditCount;
export const selectAccessToken = (state: RootState) => state.ui.accessToken;
export const selectIsPremiumUser = (state: RootState) => state.ui.isPremiumUser;
export const selectIsOutOfCreditsModalOpen = (state: RootState) =>
  state.ui.isOutOfCreditsModalOpen;
export const selectIsAuthenticationOpen = (state: RootState) =>
  state.ui.isAuthenticationOpen;
export const selectIsExplorePlansOverlayOpen = (state: RootState) =>
  state.ui.isExplorePlansOverlayOpen;
export const selectIsResumeDownloading = (state: RootState) =>
  state.ui.isResumeDownloading;
export const selectDodoRedirectUrl = (state: RootState) =>
  state.ui.dodoRedirectUrl;
export const selectIsScoringModalOpen = (state: RootState) =>
  state.ui.isScoringLoadingModalOpen;

export default uiSlice.reducer;
